import React from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Alert,
  Skeleton,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import {
  subscriptionHistoryQuery,
  adminStartPlanTierTrialMutation,
  adminExtendPlanTierTrialMutation,
  adminCancelPlanTierTrialMutation,
  adminDeletePlanTierTrialMutation,
} from '../../graphql';
import {
  SubscriptionItem,
  Subscription as SubscriptionType,
} from './SubscriptionItem';
import { AddOnPlanItem } from './AddOnPlanItem';
import {
  AdminVenueQuery,
  AdminExtendVenueSubscriptionMutation,
  AdminExtendVenueSubscriptionMutationVariables,
  AdminStartPlanTierTrialMutation,
  AdminStartPlanTierTrialMutationVariables,
  AdminExtendPlanTierTrialMutation,
  AdminExtendPlanTierTrialMutationVariables,
  AdminCancelPlanTierTrialMutation,
  AdminCancelPlanTierTrialMutationVariables,
  AdminDeletePlanTierTrialMutation,
  AdminDeletePlanTierTrialMutationVariables,
} from '@pv/common/graphql/operations';
import { enqueueSnackbar } from 'notistack';
import { Calendar, CreditCard, Layers, Award } from 'react-feather';

const adminExtendVenueSubscriptionMutation = gql`
  mutation AdminExtendVenueSubscription(
    $input: AdminExtendVenueSubscriptionInput!
  ) {
    adminExtendVenueSubscription(input: $input) {
      adminVenue {
        id
        subscription {
          id
          status
        }
      }
      errors {
        message
      }
    }
  }
`;
interface SubscriptionPanelProps {
  venue: AdminVenueQuery['adminVenue'];
}

export const SubscriptionPanel = ({ venue }: SubscriptionPanelProps) => {
  const variables = { venueId: venue.id };
  const { data, loading, error } = useQuery(subscriptionHistoryQuery, {
    variables,
  });

  const activeSubscription = venue.subscription;
  const upcomingSubscription = venue.upcomingSubscription;
  const subscriptionHistory = data?.subscriptionHistory
    .filter(
      (subscription: SubscriptionType) =>
        ![activeSubscription?.id, upcomingSubscription?.id].includes(
          subscription.id
        )
    )
    .sort((a: SubscriptionType, b: SubscriptionType) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  const [adminExtendVenueSubscription] = useMutation<
    AdminExtendVenueSubscriptionMutation,
    AdminExtendVenueSubscriptionMutationVariables
  >(adminExtendVenueSubscriptionMutation, {
    onCompleted: (data) => {
      if (
        data?.adminExtendVenueSubscription?.adminVenue?.subscription?.status ===
        'active'
      ) {
        enqueueSnackbar('Subscription extended successfully', {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          `Error extending subscription: ${data?.adminExtendVenueSubscription?.errors[0]?.message}`,
          {
            variant: 'error',
          }
        );
      }
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error extending subscription', {
        variant: 'error',
      });
    },
  });

  const extendSubscription = () => {
    adminExtendVenueSubscription({
      variables: {
        input: { id: venue.id },
      },
    });
  };

  const [startTierTrial, { loading: startTrialLoading }] = useMutation<
    AdminStartPlanTierTrialMutation,
    AdminStartPlanTierTrialMutationVariables
  >(adminStartPlanTierTrialMutation, {
    onCompleted: (data) => {
      const errors = data?.adminStartPlanTierTrial?.errors;
      if (errors && errors.length > 0) {
        enqueueSnackbar(`Error starting trial: ${errors[0]?.message}`, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Tier trial started successfully', {
          variant: 'success',
        });
      }
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error starting tier trial', { variant: 'error' });
    },
  });

  const [extendTierTrial, { loading: extendTrialLoading }] = useMutation<
    AdminExtendPlanTierTrialMutation,
    AdminExtendPlanTierTrialMutationVariables
  >(adminExtendPlanTierTrialMutation, {
    onCompleted: (data) => {
      const errors = data?.adminExtendPlanTierTrial?.errors;
      if (errors && errors.length > 0) {
        enqueueSnackbar(`Error extending trial: ${errors[0]?.message}`, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Tier trial extended by 7 days', {
          variant: 'success',
        });
      }
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error extending tier trial', { variant: 'error' });
    },
  });

  const [cancelTierTrial, { loading: cancelTrialLoading }] = useMutation<
    AdminCancelPlanTierTrialMutation,
    AdminCancelPlanTierTrialMutationVariables
  >(adminCancelPlanTierTrialMutation, {
    onCompleted: (data) => {
      const errors = data?.adminCancelPlanTierTrial?.errors;
      if (errors && errors.length > 0) {
        enqueueSnackbar(`Error canceling trial: ${errors[0]?.message}`, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Tier trial canceled successfully', {
          variant: 'success',
        });
      }
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error canceling tier trial', { variant: 'error' });
    },
  });

  const [deleteTierTrial, { loading: deleteTrialLoading }] = useMutation<
    AdminDeletePlanTierTrialMutation,
    AdminDeletePlanTierTrialMutationVariables
  >(adminDeletePlanTierTrialMutation, {
    onCompleted: (data) => {
      const errors = data?.adminDeletePlanTierTrial?.errors;
      if (errors && errors.length > 0) {
        enqueueSnackbar(`Error deleting trial: ${errors[0]?.message}`, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Tier trial deleted successfully', {
          variant: 'success',
        });
      }
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error deleting tier trial', { variant: 'error' });
    },
  });

  const handleCancelTierTrial = () => {
    cancelTierTrial({
      variables: { input: { venueId: venue.id } },
    });
  };

  const handleDeleteTierTrial = () => {
    deleteTierTrial({
      variables: { input: { venueId: venue.id } },
    });
  };

  const handleStartTierTrial = (trialTier: string) => {
    startTierTrial({
      variables: { input: { venueId: venue.id, trialTier } },
    });
  };

  const handleExtendTierTrial = () => {
    extendTierTrial({
      variables: { input: { venueId: venue.id, additionalDays: 7 } },
    });
  };

  const previouslyTrialedTiers = new Set(
    (venue.planTierTrialHistory || []).map((t) => t.trialTier.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Stack spacing={3}>
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: 2 }}
          />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading subscription data: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="600" gutterBottom>
              Subscription Management
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {activeSubscription && (
                <Chip
                  label={`${startCase(activeSubscription?.status || '')}`}
                  color={
                    activeSubscription?.status === 'active'
                      ? 'success'
                      : 'warning'
                  }
                />
              )}
            </Stack>
          </Box>

          {activeSubscription?.status !== 'active' && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={extendSubscription}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Extend Subscription for ACH Payment Only
            </Button>
          )}
        </Stack>
      </Paper>

      <Stack spacing={4}>
        {/* Current & Upcoming Subscriptions */}
        {(activeSubscription || upcomingSubscription) && (
          <Box>
            <Typography
              variant="h5"
              fontWeight="600"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
            >
              <Calendar size={20} />
              Active Subscriptions
            </Typography>

            <Stack spacing={3}>
              {activeSubscription && (
                <SubscriptionItem
                  subscription={activeSubscription}
                  header="Current"
                />
              )}
              {upcomingSubscription && (
                <SubscriptionItem
                  subscription={upcomingSubscription}
                  header="Upcoming"
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Add-on subscriptions */}
        {venue.addOnPlans && venue.addOnPlans.length > 0 && (
          <Box>
            <Typography
              variant="h5"
              fontWeight="600"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
            >
              <Layers size={20} />
              Add-on subscriptions
            </Typography>

            <Stack spacing={3}>
              {venue.addOnPlans.map((addOnPlan) => (
                <AddOnPlanItem key={addOnPlan.id} addOnPlan={addOnPlan} />
              ))}
            </Stack>
          </Box>
        )}

        {/* Plan Tier Trials */}
        <Box>
          <Typography
            variant="h5"
            fontWeight="600"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
          >
            <Award size={20} />
            Plan Tier Trials
          </Typography>

          {/* Grant Trial Buttons */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              GRANT TIER TRIAL
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              {['professional', 'premium', 'enterprise'].map((tier) => (
                <Button
                  key={tier}
                  variant="outlined"
                  disabled={
                    startTrialLoading || venue.subscription?.tier === tier
                  }
                  onClick={() => handleStartTierTrial(tier)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  {startCase(tier)}
                  {previouslyTrialedTiers.has(tier) ? ' (re-grant)' : ''}
                </Button>
              ))}
            </Stack>
          </Paper>

          {/* Active Trial */}
          {venue.activePlanTierTrial && (
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    ACTIVE TRIAL
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h6" fontWeight="600">
                      {startCase(venue.activePlanTierTrial.trialTier)} Trial
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {moment(venue.activePlanTierTrial.startDate).format(
                      'MMM DD, YYYY'
                    )}
                    {' - '}
                    {venue.activePlanTierTrial.endDate
                      ? moment(venue.activePlanTierTrial.endDate).format(
                          'MMM DD, YYYY'
                        )
                      : 'No end date'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={extendTrialLoading}
                    onClick={handleExtendTierTrial}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Extend 7 Days
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={cancelTrialLoading}
                    onClick={handleCancelTierTrial}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Cancel Upgrade
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={deleteTrialLoading}
                    onClick={handleDeleteTierTrial}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Delete Trial
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* Trial History Table */}
          {venue.planTierTrialHistory &&
            venue.planTierTrialHistory.length > 0 && (
              <TableContainer
                component={Paper}
                elevation={1}
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Trial Tier</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Start Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>End Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Granted By</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {venue.planTierTrialHistory.map((trial) => (
                      <TableRow key={trial.id}>
                        <TableCell>{startCase(trial.trialTier)}</TableCell>
                        <TableCell>
                          <Chip
                            label={startCase(trial.status || '')}
                            color={
                              trial.status === 'active'
                                ? 'success'
                                : trial.status === 'canceled'
                                ? 'error'
                                : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {trial.startDate
                            ? moment(trial.startDate).format('MMM DD, YYYY')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {trial.endDate
                            ? moment(trial.endDate).format('MMM DD, YYYY')
                            : '-'}
                        </TableCell>
                        <TableCell>{trial.grantedBy || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Box>

        {/* Subscription History */}
        <Box>
          <Typography
            variant="h5"
            fontWeight="600"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
          >
            Previous Subscriptions
          </Typography>

          {!subscriptionHistory || subscriptionHistory.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <Typography color="text.secondary" variant="body1">
                No subscription history available
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {subscriptionHistory.map(
                (subscription: SubscriptionType, index: number) => (
                  <SubscriptionItem
                    key={subscription.id}
                    subscription={subscription}
                    header={`${subscription.planName} ${startCase(
                      subscription.billingInterval
                    )}  (${subscription.status})`}
                  />
                )
              )}
            </Stack>
          )}
        </Box>

        {/* No subscriptions at all */}
        {!activeSubscription &&
          !upcomingSubscription &&
          (!subscriptionHistory || subscriptionHistory.length === 0) && (
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <CreditCard
                size={48}
                style={{ marginBottom: 16, color: '#9e9e9e' }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Subscriptions Found
              </Typography>
              <Typography color="text.secondary">
                This venue has no current, upcoming, or historical
                subscriptions.
              </Typography>
            </Paper>
          )}
      </Stack>
    </Box>
  );
};
